/**
 * Small fetch wrapper for the public marketing forms (CX-397). It exists so the start page
 * does not download axios, its interceptors and the auth stores. Authenticated code keeps
 * using `apiClient`.
 */

const PUBLIC_API_BASE = '/api/v1/public';

export class PublicFetchError extends Error {
  readonly status: number;
  readonly code?: string;
  /** The parsed JSON error body, when there was one. */
  readonly data?: unknown;

  constructor(message: string, status: number, code?: string, data?: unknown) {
    super(message);
    this.name = 'PublicFetchError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

type Params = Record<string, string | number | boolean | undefined>;

function withParams(path: string, params?: Params): string {
  if (!params) return path;
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const text = query.toString();
  return text ? `${path}?${text}` : path;
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  options: { body?: unknown; params?: Params; blob?: boolean } = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${PUBLIC_API_BASE}${withParams(path, options.params)}`, {
      method,
      headers: { 'Content-Type': 'application/json', Accept: options.blob ? '*/*' : 'application/json' },
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    });
  } catch {
    throw new PublicFetchError('Network error', 0);
  }

  if (options.blob && response.ok) return (await response.blob()) as T;

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // Non-JSON body (proxy error page, empty 204): handled by the status check below.
  }

  if (!response.ok) {
    const data = payload as { error?: string; code?: string } | null;
    throw new PublicFetchError(data?.error ?? `Request failed (${response.status})`, response.status, data?.code, payload);
  }
  return payload as T;
}

/** GET `/api/v1/public/<path>`; resolves to the `data` field of the response envelope. */
export async function publicGet<T>(path: string, params?: Params): Promise<T> {
  return (await request<{ data: T }>('GET', path, { params })).data;
}

/** POST JSON to `/api/v1/public/<path>`; resolves to the `data` field of the response envelope. */
export async function publicPost<T>(path: string, body?: unknown, params?: Params): Promise<T> {
  return (await request<{ data: T }>('POST', path, { body, params })).data;
}

/** POST that resolves to the whole response body (for endpoints that answer without a `data` envelope). */
export function publicPostRaw<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, { body });
}

/** GET a file (PDF, calendar invite) as a Blob. */
export function publicGetBlob(path: string): Promise<Blob> {
  return request<Blob>('GET', path, { blob: true });
}
